<template>
  <v-container fluid class="timeline-page pa-4 pa-md-6">
    <v-card flat class="timeline-shell">
      <div class="timeline-hero">
        <div>
          <div class="eyebrow">Sistema</div>
          <h1 class="timeline-title">Novedades del sistema</h1>
          <div class="timeline-subtitle">
            Actualizaciones y correcciones publicadas · v{{ appVersion }}
          </div>
        </div>

        <v-spacer></v-spacer>

        <v-btn
          v-if="puedePublicar"
          small
          depressed
          color="success"
          class="action-btn mr-2"
          @click="abrirDialogPublicar"
        >
          <v-icon left small>mdi-plus-circle-outline</v-icon>
          Publicar novedad
        </v-btn>

        <v-btn
          small
          depressed
          color="primary"
          class="action-btn"
          :loading="cargar"
          @click="consultarNovedades"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </div>

      <v-card-text class="pa-4 pa-md-5">
        <v-row dense class="filters-card">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="search"
              label="Buscar en novedades"
              dense
              outlined
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
              class="modern-input"
              @keyup.enter="consultarNovedades"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-alert v-if="mensaje" dense type="info" class="mt-4 mb-0 rounded-lg">
          {{ mensaje }}
        </v-alert>

        <v-timeline v-if="novedades.length" dense class="modern-timeline mt-5">
          <v-timeline-item
            v-for="item in novedades"
            :key="item.id"
            color="indigo"
            small
          >
            <v-card flat class="change-card mb-3">
              <v-card-text class="pa-4">
                <div class="d-flex align-start change-card__header flex-wrap">
                  <div class="flex-grow-1">
                    <div class="change-date">
                      {{ formatearFecha(item.created_at) }}
                    </div>
                    <div class="change-title">
                      Versión {{ item.version }}
                    </div>
                    <div class="change-meta">
                      Publicado por {{ item.usuario_nombre || 'Usuario del sistema' }}
                    </div>
                    <div v-if="item.vista" class="view-pill mt-2">
                      <v-icon x-small color="indigo" class="mr-1">mdi-monitor-dashboard</v-icon>
                      {{ item.vista }}
                    </div>
                  </div>
                  <v-chip small color="indigo" text-color="white" class="status-chip">
                    v{{ item.version }}
                  </v-chip>
                </div>

                <div class="change-reason mt-3">
                  {{ item.correccion }}
                </div>

                <div class="ack-row mt-3">
                  <div class="ack-avatars">
                    <v-tooltip
                      v-for="enterado in enteradosVisibles(item)"
                      :key="enterado.id_usuario_erp"
                      bottom
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-avatar
                          size="30"
                          color="indigo lighten-5"
                          class="ack-avatar"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <span class="ack-avatar__text">{{ iniciales(enterado.usuario_nombre) }}</span>
                        </v-avatar>
                      </template>
                      <span>{{ enterado.usuario_nombre || 'Usuario' }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="enteradosRestantes(item) > 0" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-avatar
                          size="30"
                          color="grey lighten-3"
                          class="ack-avatar ack-avatar--more"
                          v-bind="attrs"
                          v-on="on"
                        >
                          <span class="ack-avatar__text ack-avatar__text--more">
                            +{{ enteradosRestantes(item) }}
                          </span>
                        </v-avatar>
                      </template>
                      <span>{{ enteradosRestantes(item) }} personas restantes</span>
                    </v-tooltip>
                    <span v-if="!(item.enterados || []).length" class="caption grey--text">
                      Sin enterados
                    </span>
                  </div>

                  <v-spacer></v-spacer>

                  <v-btn
                    v-if="!item.enterado_usuario"
                    small
                    depressed
                    color="primary"
                    class="ack-btn"
                    :loading="enterandoId === item.id"
                    @click="marcarEnterado(item)"
                  >
                    <v-icon left small>mdi-check-circle-outline</v-icon>
                    Enterado
                  </v-btn>
                  <v-chip v-else small color="green lighten-5" text-color="green darken-2" class="font-weight-bold">
                    <v-icon left small color="green darken-2">mdi-check-circle</v-icon>
                    Enterado
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>

        <div v-else class="empty-state">
          <v-icon large color="primary">mdi-new-box</v-icon>
          <div class="empty-state__title">Sin novedades publicadas</div>
          <div class="empty-state__text">
            Aquí aparecerán las actualizaciones y correcciones del sistema.
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Publicar novedad -->
    <v-dialog v-model="dialogPublicar" max-width="640" persistent>
      <v-card>
        <v-card-title class="py-3">
          Publicar novedad del sistema
          <v-spacer></v-spacer>
          <v-btn icon @click="cerrarDialogPublicar"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                :value="nombrePublicador"
                label="Publicado por"
                dense
                outlined
                readonly
                hide-details
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                :value="fechaPublicacionPreview"
                label="Fecha y hora de publicación"
                dense
                outlined
                readonly
                hide-details
                class="modern-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.version"
                label="Número de versión"
                dense
                outlined
                readonly
                hide-details
                hint="Se asigna automáticamente al publicar"
                persistent-hint
                class="modern-input"
                :loading="cargandoVersion"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-combobox
                v-model="form.vista"
                :items="opcionesVistas"
                label="Vista donde se realizaron los cambios"
                dense
                outlined
                clearable
                hide-details
                class="modern-input"
                placeholder="Selecciona una vista o escribe una nueva..."
              ></v-combobox>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.correccion"
                label="Corrección / descripción del cambio"
                dense
                outlined
                rows="6"
                auto-grow
                hide-details
                class="modern-input"
                placeholder="Describe qué se corrigió o qué incluye esta actualización..."
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="cerrarDialogPublicar">Cancelar</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="guardando"
            :disabled="!form.correccion || !form.correccion.trim()"
            @click="publicarNovedad"
          >
            Publicar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <Alerta v-if="respuestaAlerta" :parametros="parametros" />
    <carga v-if="cargar" />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import Alerta from '@/components/alertas/Alerta.vue'
import carga from '@/components/alertas/carga.vue'
import validarErrores from '@/mixins/validarErrores'
import appVersion from '@/config/appVersion'

var moment = require('moment')
moment.locale('es')

var EDITORES_IDERP = [1, 334, 9210]

export default {
  name: 'TimelineCambios',

  components: {
    Alerta,
    carga,
  },

  mixins: [validarErrores],

  data: function () {
    return {
      appVersion: appVersion,
      search: '',
      novedades: [],
      mensaje: '',
      dialogPublicar: false,
      guardando: false,
      enterandoId: null,
      cargandoVersion: false,
      form: {
        version: '',
        vista: '',
        correccion: '',
      },
    }
  },

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),

    puedePublicar: function () {
      var u = this.getdatosUsuarioERPERP || {}
      return EDITORES_IDERP.indexOf(Number(u.iderp)) >= 0
    },

    nombrePublicador: function () {
      var u = this.getdatosUsuarioERPERP || {}
      var nombre = [u.nombre_usuario, u.apellido_usuario].filter(Boolean).join(' ').trim()
      return nombre || u.nombre_completo || u.usuario || u.email || '—'
    },

    fechaPublicacionPreview: function () {
      return moment().format('DD/MM/YYYY HH:mm')
    },

    opcionesVistas: function () {
      var menu = (this.$store && this.$store.state && this.$store.state.login)
        ? this.$store.state.login.menu || []
        : []
      var vistas = []
      var agregar = function (item) {
        if (!item) return
        if (item.path) {
          vistas.push(item.title ? item.title + ' (' + item.path + ')' : item.path)
        }
        if (Array.isArray(item.items)) {
          item.items.forEach(agregar)
        }
      }
      menu.forEach(agregar)
      return vistas.filter(function (vista, index, arr) {
        return vista && arr.indexOf(vista) === index
      }).sort()
    },
  },

  created: function () {
    this.consultarNovedades()
    this.marcarNovedadesVistas()
  },

  methods: {
    marcarNovedadesVistas: function () {
      try {
        var hoy = (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)
        localStorage.setItem('timeline_novedades_visto', hoy)
      } catch (e) { /* ignore */ }
    },
    consultarNovedades: function () {
      var self = this
      this.mensaje = ''
      this.cargar = true

      return this.$http
        .get('timeline-sistema.list', {
          params: {
            busqueda: this.search || undefined,
            limit: 100,
          },
        })
        .then(function (response) {
          self.novedades = Array.isArray(response.data) ? response.data : []
          if (!self.novedades.length) {
            self.mensaje = 'Aún no hay novedades publicadas en el sistema.'
          }
        })
        .catch(function (error) {
          var msg = error && error.response && error.response.data
            ? error.response.data.message
            : 'No fue posible consultar las novedades.'
          self.validarErrorDirecto(msg)
        })
        .finally(function () {
          self.cargar = false
        })
    },

    abrirDialogPublicar: function () {
      var self = this
      this.form.correccion = ''
      this.form.version = ''
      this.form.vista = ''
      this.dialogPublicar = true
      this.cargandoVersion = true

      this.$http
        .get('timeline-sistema.siguiente-version', {
          params: { version_base: this.appVersion },
        })
        .then(function (response) {
          self.form.version = (response.data && response.data.version) || self.appVersion
        })
        .catch(function () {
          self.form.version = self.appVersion
        })
        .finally(function () {
          self.cargandoVersion = false
        })
    },

    cerrarDialogPublicar: function () {
      if (this.guardando) return
      this.dialogPublicar = false
    },

    publicarNovedad: function () {
      var self = this
      var texto = (this.form.correccion || '').trim()
      if (!texto) {
        this.validarErrorDirecto('Escribe la corrección o descripción del cambio.')
        return
      }

      this.guardando = true
      this.$http
        .post('timeline-sistema.add', {
          correccion: texto,
          version: this.form.version,
          version_base: this.appVersion,
          vista: this.form.vista,
        })
        .then(function () {
          self.dialogPublicar = false
          self.consultarNovedades()
          self.$root.$emit('timeline-novedades-actualizado')
        })
        .catch(function (error) {
          var msg = error && error.response && error.response.data
            ? error.response.data.message
            : 'No fue posible publicar la novedad.'
          self.validarErrorDirecto(msg)
        })
        .finally(function () {
          self.guardando = false
        })
    },

    marcarEnterado: function (item) {
      var self = this
      this.enterandoId = item.id

      this.$http
        .post('timeline-sistema.enterado/' + item.id)
        .then(function () {
          return self.consultarNovedades()
        })
        .catch(function (error) {
          var msg = error && error.response && error.response.data
            ? error.response.data.message
            : 'No fue posible registrar el enterado.'
          self.validarErrorDirecto(msg)
        })
        .finally(function () {
          self.enterandoId = null
        })
    },

    iniciales: function (nombre) {
      var partes = String(nombre || 'U')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
      if (!partes.length) return 'U'
      var primera = partes[0].charAt(0)
      var segunda = partes.length > 1 ? partes[partes.length - 1].charAt(0) : ''
      return (primera + segunda).toUpperCase()
    },

    enteradosVisibles: function (item) {
      var enterados = Array.isArray(item.enterados) ? item.enterados : []
      return enterados.slice(0, 10)
    },

    enteradosRestantes: function (item) {
      var enterados = Array.isArray(item.enterados) ? item.enterados : []
      return Math.max(enterados.length - 10, 0)
    },

    formatearFecha: function (fecha) {
      if (!fecha) return 'Sin fecha'
      return moment(fecha).isValid()
        ? moment(fecha).format('DD/MM/YYYY HH:mm')
        : fecha
    },
  },
}
</script>

<style scoped>
.timeline-page {
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 52%);
  min-height: calc(100vh - 72px);
}

.timeline-shell {
  border: 1px solid #edf2f7;
  border-radius: 22px;
  box-shadow: 0 14px 36px rgba(59, 130, 246, .08);
  overflow: hidden;
}

.timeline-hero {
  align-items: center;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 55%, #ede9fe 100%);
  color: #312e81;
  display: flex;
  gap: 16px;
  padding: 22px 26px;
}

.eyebrow {
  font-size: .7rem;
  font-weight: 700;
  letter-spacing: .12em;
  color: #6366f1;
  text-transform: uppercase;
}

.timeline-title {
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 2px 0 4px;
}

.timeline-subtitle {
  font-size: .88rem;
  color: #64748b;
}

.action-btn {
  border-radius: 999px;
  font-weight: 700;
  text-transform: none;
  box-shadow: 0 8px 18px rgba(59, 130, 246, .18) !important;
}

.filters-card {
  background: #fbfdff;
  border: 1px solid #edf2f7;
  border-radius: 16px;
  margin: 0;
  padding: 12px;
}

.modern-input ::v-deep .v-input__slot {
  background: #fff !important;
  border-radius: 12px;
}

.modern-timeline ::v-deep .v-timeline-item__body {
  max-width: none;
}

.change-card {
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 18px;
  box-shadow: 0 8px 22px rgba(99, 102, 241, .07);
}

.change-card__header {
  gap: 12px;
}

.change-date {
  color: #64748b;
  font-size: .76rem;
  font-weight: 700;
}

.change-title {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.35;
  margin-top: 2px;
}

.change-meta {
  color: #64748b;
  font-size: .86rem;
  margin-top: 4px;
}

.view-pill {
  align-items: center;
  background: #eef2ff;
  border-radius: 999px;
  color: #4338ca;
  display: inline-flex;
  font-size: .76rem;
  font-weight: 700;
  padding: 5px 9px;
}

.status-chip {
  font-weight: 700;
}

.change-reason {
  background: #f8fafc;
  border-left: 3px solid #6366f1;
  border-radius: 10px;
  color: #334155;
  font-size: .9rem;
  line-height: 1.55;
  padding: 12px 14px;
  white-space: pre-line;
}

.ack-row {
  align-items: center;
  display: flex;
  gap: 12px;
}

.ack-avatars {
  align-items: center;
  display: flex;
  min-height: 32px;
}

.ack-avatar {
  border: 2px solid #fff;
  margin-right: -8px;
}

.ack-avatar--more {
  box-shadow: inset 0 0 0 1px #cbd5e1;
}

.ack-avatar__text {
  color: #4338ca;
  font-size: .72rem;
  font-weight: 800;
}

.ack-avatar__text--more {
  color: #475569;
}

.ack-btn {
  border-radius: 999px;
  font-weight: 700;
  text-transform: none;
}

.empty-state {
  align-items: center;
  border: 1px dashed #cbd5e1;
  border-radius: 18px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 20px;
  min-height: 260px;
  text-align: center;
}

.empty-state__title {
  color: #1e293b;
  font-weight: 800;
  margin-top: 10px;
}

.empty-state__text {
  font-size: .88rem;
  margin-top: 4px;
}

@media (max-width: 720px) {
  .timeline-hero {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
